import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, ListChecks, Network, Search } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useDebounce } from '@/hooks/useDebounce';
import { supabase } from '@/integrations/supabase/client';
import { invokeProtectedFunction } from '@/lib/protectedFunctionAuth';
import { adminMarketplaceHref } from '@/lib/onboardingRepairCta';
import { getLastAdminDashboardRoute } from '@/lib/adminNavigationState';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CUSTOMER_PROFILE_LABEL, SUPPLIER_PROFILE_LABEL } from '@/lib/surfaceViewLabel';
import { surfaceTonePalette, type SurfaceTone } from '@/lib/surfaceTonePalette';
import {
  rankCommandPaletteItems,
  type CommandPaletteItem,
  type CommandPaletteSection,
} from '@/lib/commandPaletteRoutes';
import { clearAnalyticsNavigation, recordAnalyticsNavigation } from '@/lib/analyticsNavigation';
import { getSessionLockState } from '@/lib/auth';
import {
  hasOpenDialogLayer,
  isEditableEventContext,
  isSettingsShortcutEvent,
} from '@/lib/settingsShortcut';
import type { OrganizationSwitchRequest } from '@/contexts/OrganizationContext';

interface AppCommandPaletteProps {
  collapsed: boolean;
  items: CommandPaletteItem[];
  organizationId?: string;
  isMSP: boolean;
  tone?: SurfaceTone;
  onOpenCustomerView?: (request: OrganizationSwitchRequest) => Promise<void> | void;
  /**
   * Super-admin only (admin console): lazily search marketplace listings by
   * company name / domain via the super-admin listings read model. Results
   * deep-link straight to the listing dialog's Scan Engine tab (ENT-2340).
   */
  enableAdminListingSearch?: boolean;
  /**
   * ENT-3704: destination for the platform-standard settings shortcut
   * (`Cmd+,` / `Ctrl+,`). Defaults to the main-app settings surface; the
   * supplier/vendor portal passes its own `/vendor/settings` route.
   */
  settingsShortcutPath?: string;
}

const sectionOrder: CommandPaletteSection[] = [
  'Admin',
  'Listings',
  'Pages',
  'Actions',
  'Customers',
  'Suppliers',
  SUPPLIER_PROFILE_LABEL,
];
const recentItemsStorageKey = 'app-command-palette-recents';
const maxRecentItemCount = 10;

// ENT-2340: reuse the canonical super-admin listing typeahead (the same
// searchListings surface the onboarding-review merge-target picker and the
// ENT-1751 AI-research approval use) instead of a bespoke marketplace query.
// It matches on name/primary-domain AND the linked supplier_domains estate,
// including apex resolution (a subdomain query resolves to the listing that
// owns the registrable apex).
const ADMIN_ONBOARDING_REVIEW_READ_MODEL = 'admin-onboarding-review-read-model';
const adminListingSearchLimit = 8;

interface AdminListingSearchRow {
  id: string;
  company_name: string | null;
  domain: string | null;
  status?: string | null;
}

interface AdminListingSearchResponse {
  listings?: AdminListingSearchRow[];
}

// Single source for the cmdk item identity so the controlled selection value
// always matches the rendered CommandItem value.
function getCommandItemValue(item: CommandPaletteItem): string {
  return `${item.title} ${item.subtitle} ${item.pathLabel}`;
}

export function AppCommandPalette({
  collapsed,
  items,
  organizationId,
  isMSP,
  tone = 'workspace',
  onOpenCustomerView,
  enableAdminListingSearch = false,
  settingsShortcutPath = '/settings',
}: AppCommandPaletteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [entityItems, setEntityItems] = useState<CommandPaletteItem[]>([]);
  const [isEntityLoading, setIsEntityLoading] = useState(false);
  const [recentItemIds, setRecentItemIds] = useState<string[]>([]);
  const debouncedQuery = useDebounce(query, 200);
  const hasSupplierSearch = Boolean(organizationId);
  const hasCustomerSearch = Boolean(isMSP && organizationId);
  const toneConfig = surfaceTonePalette[tone];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) {
        return;
      }

      if (getSessionLockState()) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      setOpen((current) => !current);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // ENT-3704: platform-standard settings shortcut (Cmd+, / Ctrl+,). Mirrors the
  // ⌘K listener's guards — ignored while a session reauth lock is active — and
  // additionally defers to text-entry focus and any open dialog so it never
  // hijacks typing or fires from inside a modal (incl. this palette).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSettingsShortcutEvent(event)) {
        return;
      }

      if (isEditableEventContext(event) || hasOpenDialogLayer()) {
        return;
      }

      event.preventDefault();

      if (getSessionLockState()) {
        return;
      }

      navigate(settingsShortcutPath);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, settingsShortcutPath]);

  useEffect(() => {
    setOpen(false);
    setQuery('');
    setEntityItems([]);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedValue = window.localStorage.getItem(recentItemsStorageKey);
    if (!storedValue) {
      return;
    }

    try {
      const parsedValue = JSON.parse(storedValue);
      if (Array.isArray(parsedValue)) {
        setRecentItemIds(parsedValue.filter((value): value is string => typeof value === 'string'));
      }
    } catch {
      window.localStorage.removeItem(recentItemsStorageKey);
    }
  }, []);

  useEffect(() => {
    const hasEntitySearch = Boolean(organizationId) || enableAdminListingSearch;
    if (!open || !hasEntitySearch || debouncedQuery.trim().length < 2) {
      setEntityItems([]);
      setIsEntityLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchEntities = async () => {
      setIsEntityLoading(true);
      const nextItems: CommandPaletteItem[] = [];
      const searchTerm = `%${debouncedQuery.trim()}%`;

      if (enableAdminListingSearch) {
        try {
          const { data, error } = await invokeProtectedFunction<AdminListingSearchResponse>(
            ADMIN_ONBOARDING_REVIEW_READ_MODEL,
            { searchListings: debouncedQuery.trim() },
          );

          if (error) {
            console.error('Command palette listing search failed:', error);
          } else {
            const seenListingIds = new Set<string>();
            for (const listing of (data?.listings ?? []).slice(0, adminListingSearchLimit)) {
              if (!listing?.id || seenListingIds.has(listing.id)) {
                continue;
              }
              seenListingIds.add(listing.id);

              const title = listing.company_name || listing.domain || 'Untitled listing';
              const statusSuffix = listing.status ? ` • ${listing.status} listing` : '';
              nextItems.push({
                id: `admin-listing-${listing.id}`,
                kind: 'entity' as const,
                title,
                subtitle: `${listing.domain || 'No domain'}${statusSuffix}`,
                pathLabel: 'Entity > Admin > Marketplace Listing > Scan Engine',
                // ENT-2378: open the peek drawer (the docked slide-over editor,
                // ENT-2307) rather than the full-screen modal. The peek open path
                // is id-guarded (no ENT-2361 double-open). Carry an explicit
                // peekTab=scan-engine intent so the drawer lands on Scan Engine
                // even when it is already open on another tab — a fresh open
                // defaults to Scan Engine anyway, but an already-open drawer would
                // otherwise preserve its current tab (ENT-2378 Codex P2).
                url: adminMarketplaceHref({ peek: listing.id, peekTab: 'scan-engine' }),
                icon: ListChecks,
                section: 'Listings' as const,
                keywords: [
                  'listing',
                  'scan engine',
                  ...(listing.company_name ? [listing.company_name] : []),
                  ...(listing.domain ? [listing.domain] : []),
                ],
              });
            }
          }
        } catch (error) {
          console.error('Command palette listing search failed:', error);
        }
      }

      if (!organizationId) {
        if (!isCancelled) {
          setEntityItems(nextItems);
          setIsEntityLoading(false);
        }
        return;
      }

      const { data: suppliers } = await supabase
        .from('suppliers')
        .select('id, name, status')
        .eq('organization_id', organizationId)
        .ilike('name', searchTerm)
        .limit(5);

      nextItems.push(
        ...((suppliers || []).map((supplier) => ({
          id: `supplier-${supplier.id}`,
          kind: 'entity' as const,
          title: supplier.name,
          subtitle: `Supplier record${supplier.status ? ` • ${supplier.status}` : ''}`,
          pathLabel: 'Entity > Suppliers',
          url: `/suppliers/${supplier.id}`,
          icon: Building2,
          section: 'Suppliers' as const,
          keywords: ['supplier', supplier.name],
        }))),
      );

      nextItems.push(
        ...((suppliers || []).map((supplier) => ({
          id: `supplier-invite-${supplier.id}`,
          kind: 'action' as const,
          title: `Send Invitation to ${supplier.name}`,
          subtitle: 'Open the supplier invitation flow on the supplier record',
          pathLabel: 'Action > Supplier > Send Invitation',
          url: `/suppliers/${supplier.id}?action=send-invitation`,
          icon: Building2,
          section: 'Actions' as const,
          keywords: ['supplier invite', 'send invitation', 'verify supplier', supplier.name],
        }))),
      );

      if (isMSP) {
        const { data: customerRelations } = await supabase
          .from('msp_customer_relationships')
          .select('customer_org_id')
          .eq('msp_org_id', organizationId)
          .eq('status', 'active')
          .limit(50);

        const customerOrgIds = (customerRelations || []).map((relation) => relation.customer_org_id);

        if (customerOrgIds.length > 0) {
          const { data: customerOrgs } = await supabase
            .from('organizations')
            .select('id, name, slug')
            .in('id', customerOrgIds)
            .ilike('name', searchTerm)
            .limit(5);

          nextItems.push(
            ...((customerOrgs || []).map((customerOrg) => ({
              id: `customer-${customerOrg.id}`,
              kind: 'entity' as const,
              title: customerOrg.name,
              subtitle: 'Managed customer',
              pathLabel: 'Entity > Customers',
              url: `/customers?search=${encodeURIComponent(customerOrg.name)}`,
              icon: Network,
              section: 'Customers' as const,
              keywords: ['customer', customerOrg.name],
            }))),
          );

          if (onOpenCustomerView) {
            nextItems.push(
              ...((customerOrgs || []).map((customerOrg) => ({
                id: `customer-login-${customerOrg.id}`,
                kind: 'action' as const,
                title: `Login as ${customerOrg.name}`,
                subtitle: 'Open customer view directly',
                pathLabel: `Action > Customers > Open ${CUSTOMER_PROFILE_LABEL}`,
                url: '/dashboard',
                icon: Network,
                section: 'Actions' as const,
                keywords: [
                  'login customer',
                  'customer view',
                  'view as customer',
                  customerOrg.name,
                ],
                onSelect: () => onOpenCustomerView({
                  targetOrgId: customerOrg.id,
                  source: 'command-palette-customer',
                  targetKind: 'managed-customer',
                }),
              }))),
            );
          }
        }
      }

      if (!isCancelled) {
        setEntityItems(nextItems);
        setIsEntityLoading(false);
      }
    };

    void fetchEntities();

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery, enableAdminListingSearch, isMSP, onOpenCustomerView, open, organizationId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (typeof resultsRef.current?.scrollTo === 'function') {
      resultsRef.current.scrollTo({ top: 0 });
      return;
    }

    if (resultsRef.current) {
      resultsRef.current.scrollTop = 0;
    }
  }, [open, query]);

  const groupedItems = useMemo(() => {
    const allItems = [...items, ...entityItems];
    const rankedItems = rankCommandPaletteItems(allItems, query, recentItemIds);
    const hasQuery = query.trim().length > 0;
    const groups = new Map<CommandPaletteSection, CommandPaletteItem[]>();

    if (!hasQuery) {
      sectionOrder.forEach((section) => {
        const sectionItems = rankedItems.filter((item) => item.section === section);
        if (sectionItems.length > 0) {
          groups.set(section, sectionItems);
        }
      });
    } else {
      rankedItems
        .filter((item) => item.rank > 0)
        .forEach((item) => {
          const existingItems = groups.get(item.section) || [];
          existingItems.push(item);
          groups.set(item.section, existingItems);
        });
    }

    return [...groups.entries()].map(([section, sectionItems]) => ({
      section,
      items: sectionItems,
    }));
  }, [entityItems, items, query, recentItemIds]);

  // ENT-2340: keep the highlight on the top-ranked row whenever that row
  // changes. Entity results (listings, suppliers, customers) land
  // asynchronously after the static rows render, and without this reset cmdk
  // leaves the highlight on the previously-first static row, so pressing
  // Enter would open the wrong result. State is adjusted during render (not
  // in an effect) per the React "derive state from props" pattern.
  const firstItem = groupedItems[0]?.items[0];
  const firstItemValue = firstItem ? getCommandItemValue(firstItem) : '';
  const [previousFirstItemValue, setPreviousFirstItemValue] = useState(firstItemValue);
  if (firstItemValue !== previousFirstItemValue) {
    setPreviousFirstItemValue(firstItemValue);
    setSelectedValue(firstItemValue);
  }

  const searchPlaceholder = useMemo(() => {
    const hasAdminItems = items.some((item) => item.section === 'Admin');

    if (hasAdminItems || enableAdminListingSearch) {
      return enableAdminListingSearch
        ? 'Jump to Admin, Actions and Listings...'
        : 'Jump to Admin and Actions...';
    }

    if (hasCustomerSearch && hasSupplierSearch) {
      return 'Jump to Pages, Actions, Customers and Suppliers...';
    }

    if (hasCustomerSearch) {
      return 'Jump to Pages, Actions and Customers...';
    }

    if (hasSupplierSearch) {
      return 'Jump to Pages, Actions and Suppliers...';
    }

    return 'Jump to Pages and Actions...';
  }, [enableAdminListingSearch, hasCustomerSearch, hasSupplierSearch, items]);

  const handleSelect = async (item: CommandPaletteItem) => {
    const nextRecentItemIds = [item.id, ...recentItemIds.filter((recentId) => recentId !== item.id)]
      .slice(0, maxRecentItemCount);

    setRecentItemIds(nextRecentItemIds);
    window.localStorage.setItem(recentItemsStorageKey, JSON.stringify(nextRecentItemIds));

    if (item.onSelect) {
      if (item.navigateAfterSelect) {
        recordAnalyticsNavigation({
          source: 'command_palette',
          surface: 'app_shell',
          intent: `command_palette:${item.kind}:${item.id}`,
          to: item.url,
        });
      }

      try {
        await item.onSelect();
      } finally {
        if (!item.navigateAfterSelect) {
          clearAnalyticsNavigation();
        }
      }
    } else {
      const targetUrl = item.id === 'admin-dashboard' ? getLastAdminDashboardRoute() : item.url;
      recordAnalyticsNavigation({
        source: 'command_palette',
        surface: 'app_shell',
        intent: `command_palette:${item.kind}:${item.id}`,
        to: targetUrl,
      });
      navigate(targetUrl);
    }

    setOpen(false);
  };

  return (
    <>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex w-full items-center justify-center">
              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Open command palette"
                className={cn(
                  'flex size-8 items-center justify-center rounded-xl border p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                  toneConfig.borderClass,
                  toneConfig.bgClass,
                  toneConfig.textClass,
                  toneConfig.bgHoverClass,
                  toneConfig.iconHoverClass,
                )}
              >
                <Search className={cn('h-4 w-4 shrink-0', toneConfig.iconClass)} />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">Jump to</TooltipContent>
        </Tooltip>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open command palette"
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            toneConfig.borderClass,
            toneConfig.bgClass,
            toneConfig.textClass,
            toneConfig.bgHoverClass,
          )}
        >
          <Search className={cn('h-4 w-4 shrink-0', toneConfig.iconClass)} />
          <span className={cn('flex-1 truncate text-left text-sm', toneConfig.textMutedClass)}>
            Jump to...
          </span>
          <CommandShortcut className={cn('text-[10px]', toneConfig.shortcutClass)}>
            Ctrl+K
          </CommandShortcut>
        </button>
      )}

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        commandProps={{ shouldFilter: false, value: selectedValue, onValueChange: setSelectedValue }}
        contentClassName={cn(
          'sm:max-w-xl md:max-w-2xl',
        )}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search available screens, actions, customers, and suppliers.
        </DialogDescription>
        <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
        <CommandList ref={resultsRef} data-testid="app-command-palette-results">
          <CommandEmpty>
            {isEntityLoading
              ? 'Searching…'
              : 'No matching pages, actions, or records found.'}
          </CommandEmpty>
          {groupedItems.map((group) => (
            <CommandGroup key={group.section} heading={group.section}>
              {group.items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={getCommandItemValue(item)}
                  keywords={item.keywords}
                  onSelect={() => {
                    void handleSelect(item);
                  }}
                >
                  <item.icon className="mr-3 h-4 w-4 shrink-0" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">{item.title}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {item.subtitle}
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground/80">
                      {item.pathLabel}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
